import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Users, Calendar, MessageSquare, ArrowRight, CheckCircle, Star, Zap, Shield, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

const FEATURES = [
  {
    icon: Users,
    title: 'Find Your Perfect Partner',
    desc: 'Search members by location, interests, and preferred check-in frequency to find someone who truly matches your goals.',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    icon: Calendar,
    title: 'Shared Task Calendar',
    desc: 'Create and manage your goals on a shared calendar. Tag your partner on tasks so you both stay in the loop.',
    color: 'text-accent',
    bg: 'bg-accent/10',
  },
  {
    icon: MessageSquare,
    title: 'Real-Time Messaging',
    desc: 'Chat directly with your accountability partners. Share updates, encouragement, and images in real time.',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    icon: TrendingUp,
    title: 'Track Progress Together',
    desc: 'Mark tasks complete, update your partner, and celebrate wins side by side. Accountability made visual.',
    color: 'text-accent',
    bg: 'bg-accent/10',
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Create Your Profile', desc: 'Tell us your goals, interests, and how often you want to check in.' },
  { step: '02', title: 'Find a Partner', desc: 'Browse members and send a partner request to someone who matches your vibe.' },
  { step: '03', title: 'Set Goals Together', desc: 'Add tasks on the shared calendar and tag each other to stay aligned.' },
  { step: '04', title: 'Stay Accountable', desc: 'Check in via chat, celebrate progress, and keep each other on track.' },
];

const TESTIMONIALS = [
  {
    name: 'Sarah K.',
    role: 'Freelance Designer',
    text: 'I finally finished my online course after 3 years of trying alone. Having a partner who checks in daily made all the difference.',
    avatar: 'SK',
    stars: 5,
  },
  {
    name: 'James R.',
    role: 'Software Engineer',
    text: 'The shared calendar is genius. My partner and I tag each other on workouts — skipping feels way harder when someone is watching!',
    avatar: 'JR',
    stars: 5,
  },
  {
    name: 'Amara L.',
    role: 'MBA Student',
    text: 'I found my study partner in under 10 minutes using the filters. We have been meeting every week for 4 months now.',
    avatar: 'AL',
    stars: 5,
  },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* ── Navbar ── */}
      {/* @section: top-nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95"
        style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 8%, transparent)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center"
              style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 40%, transparent)' }}>
              <Target className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg text-foreground">Pivot-Mate</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/search')} className="rounded-xl">
                Go to App <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" className="rounded-xl">Sign In</Button>
                </Link>
                <Link to="/auth?tab=register">
                  <Button className="rounded-xl"
                    style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}>
                    Get Started Free
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      {/* @section: hero */}
      <section className="relative overflow-hidden pt-20 pb-28 px-4">
        <div className="absolute inset-0 -z-10"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% -10%, color-mix(in srgb, var(--primary) 12%, transparent), transparent)',
          }} />
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={springGentle}>
            <Badge className="mb-6 px-4 py-1.5 text-sm font-medium bg-primary/10 text-primary border-primary/20 rounded-full">
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              The accountability platform built for real people
            </Badge>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.08 }}
            className="text-5xl sm:text-6xl md:text-7xl font-bold text-foreground leading-[1.08] mb-6"
          >
            Reach your goals{' '}
            <span className="text-primary">together!</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.14 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Pivot-Mate connects you with the right accountability partner — someone who shares your goals,
            matches your pace, and shows up when it counts. Stop doing it alone.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/auth?tab=register">
              <Button size="lg" className="rounded-xl px-8 h-12 text-base font-semibold"
                style={{ boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 40%, transparent)' }}>
                Find Your Partner
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/auth">
              <Button size="lg" variant="outline" className="rounded-xl px-8 h-12 text-base font-medium">
                Sign In
              </Button>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground"
          >
            {['Free to join', 'No credit card required', 'Connect instantly'].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-primary" /> {t}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Features ── */}
      {/* @section: features */}
      <section className="py-24 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Everything you need to stay on track</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Purpose-built tools that make accountability feel natural, not forced.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springGentle, delay: i * 0.06 }}
                whileHover={{ scale: 1.02 }}
                className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4"
                style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 6%, transparent)' }}
              >
                <div className={`w-11 h-11 rounded-xl ${f.bg} flex items-center justify-center`}>
                  <f.icon className={`w-5 h-5 ${f.color}`} />
                </div>
                <h3 className="font-semibold text-foreground text-lg leading-snug">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      {/* @section: how-it-works */}
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">How it works</h2>
            <p className="text-muted-foreground text-lg">Four simple steps to finding the accountability you deserve.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springGentle, delay: i * 0.08 }}
                className="flex flex-col items-start gap-3"
              >
                <span className="text-4xl font-black text-primary/20">{step.step}</span>
                <h3 className="font-bold text-foreground text-lg">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      {/* @section: testimonials */}
      <section className="py-24 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Real people. Real results.</h2>
            <p className="text-muted-foreground text-lg">See what our community is saying.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springGentle, delay: i * 0.08 }}
                className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4"
                style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 6%, transparent)' }}
              >
                <div className="flex gap-0.5">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-foreground text-sm leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">{t.name}</p>
                    <p className="text-muted-foreground text-xs">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      {/* @section: cta-banner */}
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={springGentle}
            className="rounded-3xl p-12 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, black) 100%)',
              boxShadow: '0 20px 60px color-mix(in srgb, var(--primary) 40%, transparent)',
            }}
          >
            <div className="relative z-10">
              <Shield className="w-12 h-12 text-primary-foreground/80 mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-primary-foreground mb-4">
                Your goals deserve a witness
              </h2>
              <p className="text-primary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of people who stopped making excuses and started making progress — together.
              </p>
              <Link to="/auth?tab=register">
                <Button size="lg" variant="secondary" className="rounded-xl px-8 h-12 text-base font-semibold bg-background text-foreground hover:bg-background/90">
                  Start for Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      {/* @section: footer */}
      <footer className="border-t border-border py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-primary flex items-center justify-center">
              <Target className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
            <span className="font-bold text-foreground">Pivot-Mate</span>
          </div>
          <p className="text-muted-foreground text-sm">© 2026 Pivot-Mate. Built for goal-getters everywhere.</p>
        </div>
      </footer>
    </div>
  );
}
